import { Button, Drawer, Stack, TextField, Typography } from '@mui/material'
import { MasterFieldDef } from '@ulysses-inc/harami_api_client'
import { FC, useCallback, useId } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { gray } from 'src/features/muikds/kdsColors'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { useMutationUpdateMasterFieldDefName } from '../../api/useMutationUpdateMasterFieldDefName'

type FormData = {
  fieldDefName: string
}

type Props = {
  open: boolean
  closeDrawer: () => void
  masterUuid: string
  fieldDef: MasterFieldDef
  existingFieldDefs: Array<{ name: string; code: string }>
}

export const MasterFieldDefEditNameDrawer: FC<Props> = ({
  open,
  closeDrawer,
  masterUuid,
  fieldDef,
  existingFieldDefs,
}) => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const updateNameMutation = useMutationUpdateMasterFieldDefName(masterUuid)
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      fieldDefName: fieldDef.name,
    },
  })

  const validateFieldDefName = (value: string) => {
    if (!value) {
      return '項目名を入力してください'
    }
    if (value.length > 30) {
      return '項目名は30文字以内で入力してください'
    }
    if (
      existingFieldDefs.some(
        fd => fd.name === value && fd.code !== fieldDef.code,
      )
    ) {
      return 'この項目名は既に使用されています'
    }
    return true
  }

  const handleClose = useCallback(() => {
    reset({ fieldDefName: fieldDef.name })
    closeDrawer()
  }, [reset, closeDrawer, fieldDef.name])

  const handleDrawerClose = useModalClosePreventionIsPending(
    handleClose,
    updateNameMutation.isPending,
  )

  const onSave = useCallback(
    (data: FormData) => {
      updateNameMutation.mutate(
        {
          fieldDefCode: fieldDef.code,
          name: data.fieldDefName,
        },
        {
          onSuccess: () => {
            showSuccessSnackbar('項目名を更新しました')
            handleClose()
          },
          onError: (error: unknown) => {
            if (isHTTPError(error)) {
              switch (error.status) {
                case 400:
                case 404:
                case 409:
                  showErrorSnackbar(error.errors[0])
                  return
                default:
                  break
              }
            }
            showErrorSnackbar('項目名の更新に失敗しました')
          },
        },
      )
    },
    [
      updateNameMutation,
      fieldDef.code,
      showSuccessSnackbar,
      showErrorSnackbar,
      handleClose,
    ],
  )

  const fieldDefNameId = useId()

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 600,
        },
      }}
    >
      <Header>
        <HeaderBackButtonMui
          onClick={handleClose}
          style={{ fontWeight: 'bold', fontSize: '15px' }}
          disabled={updateNameMutation.isPending}
        />
        <HeaderTitle title="項目名を編集" />
      </Header>

      <Stack
        component="form"
        onSubmit={handleSubmit(onSave)}
        sx={{ height: '100%' }}
      >
        <Stack sx={{ flex: 1 }}>
          <Stack spacing={2} sx={{ p: 3 }}>
            <Stack spacing={1}>
              <Typography
                htmlFor={fieldDefNameId}
                component="label"
                sx={{
                  fontWeight: 'bold',
                  display: 'block',
                }}
              >
                項目名
              </Typography>
              <Controller
                name="fieldDefName"
                control={control}
                rules={{
                  validate: validateFieldDefName,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id={fieldDefNameId}
                    {...field}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    variant="outlined"
                  />
                )}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: gray[2],
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            sx={{ minWidth: 100 }}
            disabled={updateNameMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ minWidth: 100 }}
            disabled={updateNameMutation.isPending}
          >
            {updateNameMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}
